/**
 * ToolbarFilters
 * ──────────────
 * Toolbar de filtros estilo Linear/Notion. Chips de Estado / Vendedor /
 * Origen abren OverlayPanel con la lista de opciones; el chip activo
 * muestra anillo cyan 1.5px + ✕ para limpiar.
 *
 * Diseño: ver design_handoff_admin_dashboard/README.md §2.2.
 *
 * Nota: el reordering visual (activos primero, neutros después) está
 * implementado: los chips activos se rendean antes que los neutros,
 * "+ Agregar filtro" siempre al final del cluster izquierdo, "Ordenar"
 * pegado a la derecha vía margin-left: auto.
 */
import React, { useMemo, useRef, useState } from 'react';
import { OverlayPanel } from 'primereact/overlaypanel';
import { Menu } from 'primereact/menu';
import {
    toolbarFiltersCSS,
    toolbarFilterTagCSS,
    toolbarOverlayCSS,
} from './ToolbarFilters.css';

const SOURCE_OPTIONS = [
    { key: null,   label: 'Todas' },
    { key: 'pei',  label: 'Peicar' },
    { key: 'trim', label: 'Trim'   },
];

const NONE_VENDOR_KEY = 'none';

/* ─── ToolbarFilterTag (chip individual) ───────────────────────────── */

const ToolbarFilterTag = ({
    label,
    value,
    active = false,
    onOpen,           // (event) => void — abre OverlayPanel
    onClear = null,   // si se pasa, muestra ✕
}) => {
    return (
        <span css={toolbarFilterTagCSS(active)}>
            <span className="tft-label">{label}:</span>
            <button type="button" className="tft-value" onClick={onOpen}>
                <span>{value}</span>
                <i className="pi pi-chevron-down" />
            </button>
            {active && onClear && (
                <button
                    type="button"
                    className="tft-clear"
                    onClick={(e) => { e.stopPropagation(); onClear(); }}
                    aria-label={`Limpiar ${label}`}
                >
                    <i className="pi pi-times" />
                </button>
            )}
        </span>
    );
};

/* ─── Overlay de opciones (lista simple, click-to-apply) ──────────── */

const FilterOptionsOverlay = ({ panelRef, options, currentValue, onSelect, emptyMsg }) => (
    <OverlayPanel
        ref={panelRef}
        dismissable
        showCloseIcon={false}
        appendTo={typeof document !== 'undefined' ? document.body : undefined}
    >
        <div css={toolbarOverlayCSS()}>
            {options.length === 0 ? (
                <div className="tfo-empty">{emptyMsg ?? 'Sin opciones'}</div>
            ) : (
                <ul className="tfo-list">
                    {options.map((opt) => {
                        const isActive = opt.value === currentValue;
                        return (
                            <li key={String(opt.value) + opt.label}>
                                <button
                                    type="button"
                                    className={`tfo-item${isActive ? ' is-active' : ''}`}
                                    onClick={() => onSelect(opt.value)}
                                >
                                    <span>{opt.label}</span>
                                    {opt.tag && <span className="tfo-item-sub">{opt.tag}</span>}
                                    {isActive && <i className="pi pi-check tfo-check" />}
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    </OverlayPanel>
);

/* ─── ToolbarFilters (componente principal) ───────────────────────── */

const ToolbarFilters = ({
    // Estado (status)
    statusFilter = null,
    onStatusChange,
    statusOptions = [],

    // Vendedor
    vendorFilter = null,
    onVendorChange,
    vendorOptions = [],

    // Origen
    sourceFilter = null,
    onSourceChange,
    showSourceChip = false,

    // Modo Trim
    isTrimMode = false,

    // Calificadas (Trim)
    calificadas = false,
    onCalificadasChange,

    // Sort (placeholder)
    // eslint-disable-next-line no-unused-vars
    sortValue,
    // eslint-disable-next-line no-unused-vars
    onSortChange,
}) => {
    const statusOp = useRef(null);
    const vendorOp = useRef(null);
    const sourceOp = useRef(null);
    const addMenuRef = useRef(null);
    const [openedTag, setOpenedTag] = useState(null);

    /* ── derivar labels ── */
    const statusLabel = useMemo(() => {
        const found = statusOptions.find((o) => o.key === statusFilter);
        return found?.label ?? 'Todos';
    }, [statusOptions, statusFilter]);

    const vendorLabel = useMemo(() => {
        if (vendorFilter === null || vendorFilter === undefined) return 'Todos';
        if (vendorFilter === NONE_VENDOR_KEY) return 'Sin vendedor';
        const v = vendorOptions.find((vv) => String(vv.id) === String(vendorFilter));
        return v?.name ?? 'Vendedor';
    }, [vendorOptions, vendorFilter]);

    const sourceLabel = useMemo(() => {
        const found = SOURCE_OPTIONS.find((o) => o.key === sourceFilter);
        return found?.label ?? 'Todas';
    }, [sourceFilter]);

    /* ── options para los overlays ── */
    const statusItems = useMemo(
        () => statusOptions.map((o) => ({ value: o.key, label: o.label })),
        [statusOptions],
    );

    const vendorItems = useMemo(() => {
        const base = [{ value: null, label: 'Todos' }];
        if (isTrimMode) {
            base.push({ value: NONE_VENDOR_KEY, label: 'Sin vendedor' });
        }
        for (const v of vendorOptions) {
            base.push({
                value: String(v.id),
                label: v.name,
                tag: v.source ? String(v.source).toUpperCase() : null,
            });
        }
        return base;
    }, [vendorOptions, isTrimMode]);

    const sourceItems = useMemo(
        () => SOURCE_OPTIONS.map((o) => ({ value: o.key, label: o.label })),
        [],
    );

    /* ── handlers ── */
    const handleStatusSelect = (val) => {
        onStatusChange?.(val);
        statusOp.current?.hide();
    };
    const handleVendorSelect = (val) => {
        // vendor IDs viajan como number en el padre — convertimos si aplica
        let mapped = val;
        if (val !== null && val !== undefined && val !== NONE_VENDOR_KEY) {
            const num = Number(val);
            if (!Number.isNaN(num)) mapped = num;
        }
        onVendorChange?.(mapped);
        vendorOp.current?.hide();
    };
    const handleSourceSelect = (val) => {
        onSourceChange?.(val);
        sourceOp.current?.hide();
    };

    /* ── reordering: activos primero, neutros después ── */
    const statusActive = !!statusFilter;
    const vendorActive = vendorFilter !== null && vendorFilter !== undefined;
    const sourceActive = !!sourceFilter;

    const tags = [
        {
            key: 'status',
            active: statusActive,
            node: (
                <ToolbarFilterTag
                    key="status"
                    label="Estado"
                    value={statusLabel}
                    active={statusActive}
                    onOpen={(e) => { setOpenedTag('status'); statusOp.current?.toggle(e); }}
                    onClear={statusActive ? () => onStatusChange?.(null) : null}
                />
            ),
        },
    ];
    if (vendorOptions.length > 0) {
        tags.push({
            key: 'vendor',
            active: vendorActive,
            node: (
                <ToolbarFilterTag
                    key="vendor"
                    label="Vendedor"
                    value={vendorLabel}
                    active={vendorActive}
                    onOpen={(e) => { setOpenedTag('vendor'); vendorOp.current?.toggle(e); }}
                    onClear={vendorActive ? () => onVendorChange?.(null) : null}
                />
            ),
        });
    }
    if (showSourceChip) {
        tags.push({
            key: 'source',
            active: sourceActive,
            node: (
                <ToolbarFilterTag
                    key="source"
                    label="Origen"
                    value={sourceLabel}
                    active={sourceActive}
                    onOpen={(e) => { setOpenedTag('source'); sourceOp.current?.toggle(e); }}
                    onClear={sourceActive ? () => onSourceChange?.(null) : null}
                />
            ),
        });
    }
    if (isTrimMode) {
        tags.push({
            key: 'calificadas',
            active: calificadas,
            node: (
                <ToolbarFilterTag
                    key="calificadas"
                    label="Con buró"
                    value={calificadas ? 'Sí' : 'Todas'}
                    active={calificadas}
                    onOpen={() => onCalificadasChange?.()}
                    onClear={calificadas ? () => onCalificadasChange?.() : null}
                />
            ),
        });
    }

    const orderedTags = [
        ...tags.filter((t) => t.active),
        ...tags.filter((t) => !t.active),
    ];

    /* ── + Agregar filtro: reservado para futuros filtros opcionales ── */
    const addOptions = [];

    return (
        <div css={toolbarFiltersCSS()}>
            {orderedTags.map((t) => t.node)}

            {addOptions.length > 0 && (
                <>
                    <button
                        type="button"
                        className="tf-add-btn"
                        onClick={(e) => addMenuRef.current?.toggle(e)}
                    >
                        <i className="pi pi-plus" /> Agregar filtro
                    </button>
                    <Menu
                        ref={addMenuRef}
                        model={addOptions}
                        popup
                        appendTo={typeof document !== 'undefined' ? document.body : undefined}
                    />
                </>
            )}

            <FilterOptionsOverlay
                panelRef={statusOp}
                options={statusItems}
                currentValue={statusFilter}
                onSelect={handleStatusSelect}
            />
            <FilterOptionsOverlay
                panelRef={vendorOp}
                options={vendorItems}
                currentValue={
                    vendorFilter === null || vendorFilter === undefined
                        ? null
                        : (vendorFilter === NONE_VENDOR_KEY ? NONE_VENDOR_KEY : String(vendorFilter))
                }
                onSelect={handleVendorSelect}
                emptyMsg="Sin vendedores"
            />
            <FilterOptionsOverlay
                panelRef={sourceOp}
                options={sourceItems}
                currentValue={sourceFilter}
                onSelect={handleSourceSelect}
            />
        </div>
    );
};

export default ToolbarFilters;
